import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  ButtonComponent, FieldComponent, InputComponent, ModalComponent,
} from '@echospaces-web-monorepo/es-shared-ui';

import type { Content, UpdateContentDto } from './content.service';

/**
 * Edit the metadata of an existing content object. Optional file replace
 * goes here too: picking a file kicks off a versioned upload on submit
 * (server-side puts it on the same language as the previously-active
 * file, or `all` if none).
 */
@Component({
  selector: 'es-edit-content-modal',
  imports: [FormsModule, ModalComponent, ButtonComponent, FieldComponent, InputComponent],
  template: `
    <es-modal [open]="open" title="Edit content" width="sm" (closed)="onCancel()">
      <div class="p-6 space-y-4">
        <es-field label="Title" [required]="true">
          <es-input placeholder="Content title" [(ngModel)]="title"></es-input>
        </es-field>

        <es-field label="Description">
          <es-input placeholder="Optional description" [(ngModel)]="description"></es-input>
        </es-field>

        <es-field label="Replace file (optional)" hint="Upload to create a new version of the active file.">
          <input
            type="file"
            class="w-full text-sm text-[var(--text-secondary)] file:mr-3 file:px-3 file:py-1.5 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-echo-blue/20 file:text-echo-blue hover:file:bg-echo-blue/30 file:cursor-pointer"
            (change)="onFile($event)"
          />
        </es-field>

        @if (submitError()) {
          <p class="text-sm text-red-400">{{ submitError() }}</p>
        }

        <div class="flex justify-end gap-2 pt-2">
          <es-button variant="ghost" (clicked)="onCancel()">Cancel</es-button>
          <es-button
            variant="primary"
            [disabled]="submitting() || !title.trim()"
            (clicked)="onSubmit()"
          >
            {{ submitting() ? 'Saving…' : 'Save' }}
          </es-button>
        </div>
      </div>
    </es-modal>
  `,
})
export class EditContentModalComponent {
  @Input() open = false;
  @Input() set content(value: Content | null | undefined) {
    if (!value) return;
    this.title = value.title;
    this.description = value.description ?? '';
    this.pickedFile = null;
  }

  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<{ dto: UpdateContentDto; file?: File }>();

  title = '';
  description = '';
  pickedFile: File | null = null;

  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);

  onFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.pickedFile = input.files?.[0] ?? null;
  }

  onSubmit(): void {
    if (!this.title.trim()) return;
    this.submitting.set(true);
    const dto: UpdateContentDto = { title: this.title.trim() };
    if (this.description.trim()) dto.description = this.description.trim();
    this.submitted.emit({ dto, file: this.pickedFile ?? undefined });
  }

  /**
   * Parent calls after `submitted` finishes. Success resets state +
   * closes; failure surfaces the error and keeps the form populated.
   */
  finishSubmit(error?: string): void {
    this.submitting.set(false);
    this.submitError.set(error ?? null);
    if (!error) {
      this.pickedFile = null;
      this.closed.emit();
    }
  }

  onCancel(): void {
    this.submitError.set(null);
    this.closed.emit();
  }
}
